import { player, team } from "../index.ts";
import cancel from "./cancel.ts";
import { idb } from "../../db/index.ts";
import { g } from "../../util/index.ts";
import type { Negotiation, PlayerContract } from "../../../common/types.ts";
import { PHASE } from "../../../common/constants.ts";
import {
	EXCEPTION_CONFIG,
	EXCEPTION_NAMES,
	chargeException,
	evaluateExceptionSigning,
	getExceptionLedger,
	signingExceptionsActive,
	type SigningExceptionKind,
} from "../finances/exceptions.ts";

/**
 * Accept the player's offer.
 *
 * If successful, then the team's current roster will be displayed.
 *
 * @memberOf core.contractNegotiation
 * @param {number} pid An integer that must correspond with the player ID of a player in an ongoing negotiation.
 * @return {Promise.<string=>} If an error occurs, resolves to a string error message.
 */
const accept = async ({
	negotiation,
	amount,
	exp,
	dryRun,
}: {
	negotiation: Negotiation;
	amount: number;
	exp: number;
	dryRun?: boolean;
}) => {
	const salaryCapType = g.get("salaryCapType");

	// Set when this signing goes over the cap via an NBA signing exception (MLE/room/BAE), so it
	// can be charged to the team's per-season ledger after the contract is actually signed.
	let usedExceptionKind: SigningExceptionKind | undefined;

	if (salaryCapType !== "none") {
		const payroll = await team.getPayroll(g.get("userTid"));
		const birdException = negotiation.resigning && salaryCapType === "soft";

		// If this contract brings team over the salary cap, it's not a minimum contract, and it's not re-signing a current
		// player with the Bird exception, it needs a signing exception (soft cap) or it's an ERROR!
		if (
			!birdException &&
			payroll + amount - 1 > g.get("salaryCap") &&
			amount - 1 > g.get("minContract")
		) {
			if (!signingExceptionsActive()) {
				return `You cannot go over the salary cap to sign ${
					salaryCapType === "hard" ? "players" : "free agents"
				} to contracts higher than the minimum salary.`;
			}

			const { ledger, usedBaeLastSeason } = await getExceptionLedger(
				g.get("userTid"),
			);
			const result = evaluateExceptionSigning({
				payroll,
				amount,
				ledger,
				usedBaeLastSeason,
			});
			if ("error" in result) {
				return result.error;
			}

			// Exception contracts are length-limited (NBA: MLE 4 years, taxpayer MLE/BAE 2, room 3).
			const years =
				exp -
				g.get("season") +
				(g.get("phase") <= PHASE.AFTER_TRADE_DEADLINE ? 1 : 0);
			const maxYears = EXCEPTION_CONFIG.maxYears[result.kind];
			if (years > maxYears) {
				return `${EXCEPTION_NAMES[result.kind]} contracts can be at most ${maxYears} ${
					maxYears === 1 ? "year" : "years"
				} long.`;
			}

			usedExceptionKind = result.kind;
		}
	}

	// This error is for sanity checking in multi team mode. Need to check for existence of negotiation.tid because it
	// wasn't there originally and I didn't write upgrade code. Can safely get rid of it later.
	if (negotiation.tid !== undefined && negotiation.tid !== g.get("userTid")) {
		return `This negotiation was started by the ${
			g.get("teamInfoCache")[negotiation.tid]?.region
		} ${g.get("teamInfoCache")[negotiation.tid]?.name} but you are the ${
			g.get("teamInfoCache")[g.get("userTid")]?.region
		} ${
			g.get("teamInfoCache")[g.get("userTid")]?.name
		}. Either switch teams or cancel this negotiation.`;
	}

	const p = await idb.cache.players.get(negotiation.pid);
	if (!p) {
		return "Invalid pid";
	}

	// Make sure the user didn't do something in another tab to change the willingness to negotiate, such as trading away players
	const mood = await player.moodInfo(p, g.get("userTid"));
	if (!mood.willing) {
		return "Player is no longer willing to negotiate.";
	}

	// Enforce the player's asking price as a hard floor. In stock ZenGM this was
	// only enforced by the UI (generateContractOptions never offers below the
	// demand), but the mobile offer sheet calls accept directly with a free-form
	// amount — so without this check a player asking tens of millions would sign
	// for a lowball. mood.contractAmount is the mood-adjusted demand, in the same
	// thousands units as `amount`.
	if (amount < mood.contractAmount) {
		return `${p.firstName} ${p.lastName} won't sign for less than $${(
			mood.contractAmount / 1000
		).toFixed(1)}M.`;
	}

	const contract: PlayerContract = {
		amount,
		exp,
	};
	if (p.contract.rookie && g.get("phase") === PHASE.RESIGN_PLAYERS) {
		// Not sure if the phase condition is necessary. The purpose of this is for hard cap rookies with rookie contract scale.
		contract.rookie = true;
	}

	if (!dryRun) {
		await player.sign(p, g.get("userTid"), contract, g.get("phase"));
		await idb.cache.players.put(p);
		await cancel(negotiation.pid);

		if (usedExceptionKind !== undefined) {
			await chargeException(g.get("userTid"), usedExceptionKind, amount);
		}
	}
};

export default accept;
